import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const client = axios.create({ baseURL: API });

client.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("owner_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

client.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e?.response?.status === 401) {
      const url = e.config?.url || "";
      if (
        !url.includes("/auth/login") &&
        !url.includes("/auth/setup") &&
        !url.includes("/punch") &&
        !url.includes("/shop/") &&
        !url.includes("/set-pin")
      ) {
        localStorage.removeItem("owner_token");
      }
    }
    return Promise.reject(e);
  }
);

export default client;

export const formatApiError = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  if (typeof d === "object" && d.message) return d.message;
  return String(d);
};

export const getErrorOffender = (err) => {
  // Extract sequence-broken offender metadata so the UI can offer a one-tap
  // "delete the row that's blocking the sequence" action.
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && d.offender_event_id) {
    return {
      id: d.offender_event_id,
      type: d.offender_type || "",
      ts: d.offender_ts || "",
    };
  }
  return null;
};

export const fmtTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
};

export const fmtDay = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

export const dayKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

export const TYPE_LABEL = {
  in: "Punched in",
  out: "Punched out",
  break_start: "Break started",
  break_end: "Break ended",
};

export const fmtHours = (min) => {
  if (min == null) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

// Compute worked minutes (excluding breaks) for one employee's events on one day.
// Duplicate "in" events while already on the clock (from manual entries or double-taps)
// are ignored so the session isn't reset. Same for duplicate break_start / stray out.
export const workedMinutes = (dayEvents) => {
  let total = 0;
  let inAt = null;
  let breakAt = null;
  let breakTotal = 0;
  const sorted = [...dayEvents].sort(
    (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime()
  );
  for (const e of sorted) {
    const ts = new Date(e.ts).getTime();
    if (e.type === "in") {
      if (inAt === null) {
        inAt = ts;
        breakTotal = 0;
        breakAt = null;
      }
      // else: already clocked in — ignore the redundant punch
    } else if (e.type === "break_start") {
      if (inAt !== null && breakAt === null) breakAt = ts;
    } else if (e.type === "break_end") {
      if (breakAt !== null) {
        breakTotal += ts - breakAt;
        breakAt = null;
      }
    } else if (e.type === "out") {
      if (inAt !== null) {
        if (breakAt !== null) {
          // Auto-close a hanging break at the punch-out moment.
          // Matches most payroll conventions: you don't stay on break after clocking out.
          breakAt = null;
        }
        total += ts - inAt - breakTotal;
        inAt = null;
        breakTotal = 0;
      }
      // else: stray out — ignore
    }
  }
  // Show at least 1 minute for any non-zero session — matches what the owner sees
  // in the punch list (e.g. 08:25 pm → 08:26 pm reads as a minute even if the
  // underlying seconds are <30 apart).
  if (total <= 0) return 0;
  const mins = Math.round(total / 60000);
  return Math.max(1, mins);
};

// Returns true if any session in the day had a break_start that was NOT closed by
// a matching break_end. workedMinutes auto-closes such breaks at punch-out; this
// helper lets the UI surface a "break not closed" warning so owners can spot messy data.
export const hasUnclosedBreak = (dayEvents) => {
  let inAt = null;
  let breakAt = null;
  const sorted = [...dayEvents].sort(
    (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime()
  );
  for (const e of sorted) {
    if (e.type === "in") {
      if (inAt === null) inAt = e.ts;
    } else if (e.type === "break_start") {
      if (inAt !== null && breakAt === null) breakAt = e.ts;
    } else if (e.type === "break_end") {
      if (breakAt !== null) breakAt = null;
    } else if (e.type === "out") {
      if (inAt !== null) {
        if (breakAt !== null) return true; // hanging break at out
        inAt = null;
      }
    }
  }
  // Still-open break at end of the events (no out yet)
  return breakAt !== null;
};

// ISO week id like "2026-W07"
export const isoWeekId = (d = new Date()) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
};

// Start of ISO week (Mon 00:00 UTC) for a given date
export const isoWeekStart = (d = new Date()) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 1);
  return date;
};
